import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client';
import { connect, useDispatch } from 'react-redux';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import * as actionCreators from '../../actions';
import { togglePromotionModalView } from '../../actions';
import { sendAccessToken } from '../../queries/mutations';
import { getLangDefinition } from '../../utils/helpers';
import AdblockerDetector from '../Banner/BlockerDetector';
import HeaderPlaceholder from '../Placeholders/HeaderPlaceholder';
import FooterPlaceholder from '../Placeholders/FooterPlaceholder';
import Loader from '../Loader';
import { useBridgeState } from '../../bridge';
import { TabBarNavigationServer } from '../TabBarNavigation/TabBarNavigationServer';
import useBackHistoryStorage from '../../hooks/useBackHistoryStorage';
import useScrollState from '../../hooks/useScrollState';
import Icon from '../Icon';
import Logo from '../../public/static/images/brown-logo.svg';
import Plus from '../../public/static/images/green-plus.svg';
import {
  longPromotionHashes,
  shortPromotionHashes,
  urlHashes
} from '../../utils/helpers/constants';
import CloseWebview from '../../public/static/images/close-webview.svg';
import useScreenSize from '../../hooks/useScreenSize';
import { useShowFlexibleComponents } from '../../hooks/useShowFlexibleComponents';
import { useAutoScrollTop } from '../../hooks/useAutoScrollTop';
import { FlexibleHeader } from '../FlexibleHeader';
import { useTargetedBanner } from '../../hooks/useTargetedBanner';
import { TargetedBannerIds } from '../../hooks/useTargetedBanner/types';

const Banner = dynamic(() => import('../Banner'), {
  ssr: false
});

const AdManagerBanner = dynamic(() => import('../Banner/AdManagerBanner'), {
  ssr: false
});

const HeaderLight = isWebView => {
  return (
    <div className="white-header white-header--main" id="white-header">
      <a data-stat="header-logo" href="/" tabIndex="-1">
        <Icon IconComponent={Logo} width={85} height={19} />
      </a>
      {isWebView ? (
        <button className="white-header__webview-btn">
          <i>
            <Icon
              IconComponent={CloseWebview}
              width={15}
              height={15}
              indent={false}
              color={'#a9a39e'}
            />
          </i>
        </button>
      ) : (
        <a
          className="white-header__new-btn"
          href="/items/new"
          data-stat="header-new-btn"
        >
          <Icon IconComponent={Plus} width={30} height={30} indent={false} />
        </a>
      )}
    </div>
  );
};

const TabBarNavigation = dynamic(
  () => import('../TabBarNavigation').then(mod => mod.TabBarNavigation),
  {
    loading: TabBarNavigationServer,
    ssr: false
  }
);

const Footer = dynamic(() => import('../Footer'), {
  loading: FooterPlaceholder,
  ssr: false
});

const PromotionModalView = dynamic(() => import('../PromotionModalView'), {
  ssr: false
});

const SideMenuModal = dynamic(() => import('../SideMenuModal'), {
  ssr: false
});

const AuthModal = dynamic(() => import('../AuthModal'), {
  ssr: false
});

const MainLayout = dynamic(() => import('../ui/Layout'), {
  ssr: false
});

const mapStateToProps = state => {
  return {
    isVisible: state.drawerUI.isVisible,
    isSideMenuVisible: state.sideMenuModal?.isVisible,
    isPromotionModalView: state.promotionModalView?.isVisible,
    isAuthVisible: state?.authModal?.isVisible
  };
};

const Layout = ({
  isBanner,
  children,
  noBorder,
  setBlockerDetector,
  toggleSideMenuModal,
  isAuthVisible,
  isSideMenuVisible,
  isPromotionModalView,
  toggleAuthModal,
  headerTitle,
  isBottomNavigation = true,
  isShowHeader = true,
  isFixedHeader = false,
  isShowBackButton = false,
  isShowCloseButton = true,
  isCurrentItemPage = false,
  isHomePage = false,
  isSafeAreaTop = false,
  isBackgroundBanner = false,
  pageProps = {}
}) => {
  const client = useApolloClient();

  const {
    i18n: { language }
  } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const { isWebView } = useBridgeState();
  const { isScroll } = useScrollState();
  const { isSmall, isLarge } = useScreenSize();
  const { isShowFlexibleComponents } = useShowFlexibleComponents();
  const pathname = usePathname();
  const hasAuthHash = router.asPath.includes(urlHashes.authentication);

  useTargetedBanner(TargetedBannerIds.catfish);

  useEffect(() => {
    if (isSafeAreaTop && isWebView) {
      document.body.classList.add('safe-area-top');
    } else {
      document.body.classList.remove('safe-area-top');
    }
  }, [isSafeAreaTop, isWebView]);

  useEffect(() => {
    if (pageProps.apolloCache) client.cache.restore(pageProps.apolloCache);
  }, [client, pageProps.apolloCache]);

  const closeAuthModalHandler = () => {
    toggleAuthModal(false);

    router.push(`${router.asPath.split('#')[0]}`, undefined, {
      shallow: true
    });
  };

  const accessToken = router?.query.access_token;
  const [mutateToken, { loading }] = useMutation(sendAccessToken);

  useBackHistoryStorage();

  useAutoScrollTop(isCurrentItemPage, pathname);

  useMemo(() => {
    if (accessToken) {
      mutateToken({
        variables: { accessToken }
      });
    }
  }, []);

  useEffect(() => {
    toggleAuthModal(!!router.asPath.match(urlHashes.authentication));
    toggleSideMenuModal(!!router.asPath.match(urlHashes.more));

    dispatch(
      togglePromotionModalView(
        shortPromotionHashes.some(hash => router.asPath.match(hash))
      )
    );

    router?.events.on('hashChangeComplete', url => {
      toggleAuthModal(!!url.match(urlHashes.authentication));
      toggleSideMenuModal(!!url.match(urlHashes.more));

      dispatch(
        togglePromotionModalView(
          !!longPromotionHashes.some(hash => url.match(hash))
        )
      );
    });
  }, []);

  const WhiteHeader = dynamic(() => import('../WhiteHeader'), {
    loading: isHomePage ? HeaderLight.bind(this, isWebView) : HeaderPlaceholder,
    ssr: false
  });

  const getHeader = () => {
    if (isShowFlexibleComponents)
      return (
        <FlexibleHeader
          {...{
            isFixedHeader: isWebView || isFixedHeader,
            isShowCloseButton
          }}
        />
      );

    return (
      <WhiteHeader
        {...{
          noBorder,
          headerTitle,
          isFixedHeader: isWebView || isFixedHeader,
          isShowBackButton,
          isShowCloseButton
        }}
      />
    );
  };

  if (loading) return null;

  return (
    <MainLayout
      language={getLangDefinition(language)}
      {...{
        isScroll,
        isWebView
      }}
    >
      {(isLarge || (isBanner && !isSmall)) && (
        <Script
          src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
          defer
        />
      )}
      {isLarge && isBackgroundBanner && (
        <>
          <div id={TargetedBannerIds.bg} />
          <div id={TargetedBannerIds.catfish} />
          <AdManagerBanner bannerName="bg" />
          <AdManagerBanner bannerName="catfish" />
        </>
      )}
      {isBanner && !isSmall && (
        <>
          <Banner
            key="js-lotriver-desk-top-banner"
            containerClassName={'js-lotriver-desk-top-banner'}
            containerId={'js-lotriver-desk-top-banner'}
            sid={'220686'}
            bn={'1'}
            arCn={1}
          />
          <AdManagerBanner bannerName="deskTop" />
        </>
      )}
      {isShowHeader && getHeader()}
      <AdblockerDetector setBlockerDetector={setBlockerDetector} />
      {children}
      <Loader pageLoader={true} />
      {isBottomNavigation && isSmall && <TabBarNavigation />}
      {!(isWebView || isSideMenuVisible) && (
        <Footer
          isTabBarExist={isBottomNavigation}
          isBackgroundBanner={isBackgroundBanner}
        />
      )}
      {isSideMenuVisible && <SideMenuModal />}
      {isPromotionModalView && <PromotionModalView />}
      {isAuthVisible && hasAuthHash && (
        <AuthModal
          isOpen={isAuthVisible}
          modalId="auth"
          closeHandler={closeAuthModalHandler}
        />
      )}
    </MainLayout>
  );
};

Layout.propTypes = {
  noBorder: PropTypes.bool,
  children: PropTypes.node,
  toggleDrawer: PropTypes.func,
  toggleAuthModal: PropTypes.func,
  isVisible: PropTypes.bool,
  isBanner: PropTypes.bool,
  isAuthVisible: PropTypes.bool,
  isSideMenuVisible: PropTypes.bool,
  isPromotionModalView: PropTypes.bool,
  setBlockerDetector: PropTypes.func,
  toggleSideMenuModal: PropTypes.func,
  isBottomNavigation: PropTypes.bool,
  isShowHeader: PropTypes.bool,
  isFixedHeader: PropTypes.bool,
  headerTitle: PropTypes.string,
  isShowBackButton: PropTypes.bool,
  isShowCloseButton: PropTypes.bool,
  isCurrentItemPage: PropTypes.bool,
  isHomePage: PropTypes.bool,
  isSafeAreaTop: PropTypes.bool,
  isBackgroundBanner: PropTypes.bool,
  pageProps: PropTypes.object
};

export default connect(mapStateToProps, actionCreators)(Layout);
